/* eslint-disable react/jsx-no-constructed-context-values */
import App from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
    AckoMainTheme,
    ThemeProvider,
} from '@acko-tech/building-blocks.ui.theme';
import isMobile from 'ismobilejs';
import Hamburger from 'components/hamburger';
import { ViewportProvider } from 'utils/responsive';
import { initGlobals } from 'utils/globals';
import { MPosContext, FormConfigsMap } from 'utils/context';
import ErrorPage from 'widgets/errorScreen';
import SnackBar from 'library/snackbar';
import '../styles/globals.css';
import {
    NewPlanStages,
    policyDetailsInitState,
} from 'components/newPlan/constants';
import { identifyEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import { initialiseAckoAnalytics } from 'utils/globals/initialiseAckoAnalytics';
import { setTrackerIdCookie } from 'utils/cookie';
import { bootstrapUserPermissions } from 'utils/helpers/bootstrapUserPermissions';
import { fetchUserAccess } from 'utils/helpers/userAccess';
import {
    getPartnerContext,
    saveUserAccess,
} from 'utils/localStorage/partnerStorage';

declare global {
    interface Window {
        lob?: string;
    }
}

function getBootstrapFinalRoutePath({
    router,
    partnerId,
}: {
    router: any;
    partnerId: string;
}) {
    const slug = router?.query?.slug;

    if (!Array.isArray(slug) || slug.length < 3) {
        return undefined;
    }

    const [lob, routePartnerId, ...routeSegments] = slug;

    if (!lob || !routePartnerId || routePartnerId !== partnerId) {
        return undefined;
    }

    const routePath = routeSegments.join('/');

    if (!routePath) {
        return undefined;
    }

    return `/${lob}/${routePartnerId}/${routePath}`;
}

function MPosApp(props: any) {
    const { Component, pageProps, isMobileDevice, query, host, cookie } = props;

    const router = useRouter();

    const [userDetails, setUserDetails] = useState({
        user_id: '',
        address: '',
        email: '',
        name: '',
        partner_id: '',
        phone: '',
        pincode: '',
    });
    const [permissions, setPermissions] = useState({
        group: '',
        partner_id: '',
        partner_logo: '',
        permission_list: [],
        role: '',
        purchase_journey: 'v0',
    });
    const [metaData, setMetaData] = useState({
        promoter_id_visibility: false,
    });
    const [dealerType, setDealerType] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [permissionsMap, setPermissionsMap] = useState({});
    const [showHamburger, setShowHamburger] = useState(false);
    const [showErrorScreen, setShowErrorScreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [stage, setActiveStage] = useState(NewPlanStages.MODEL);
    const [planDetails, setPlanDetails] = useState(policyDetailsInitState);
    const [legalDetailsUploadedStatus, setLegalDetailsUploadedStatus] =
        useState(false);
    const [loggedUserRole, setLoggedUserRole] = useState('others');
    const [errorMessage, setErrorMessage] = useState('');
    const [gstDetailsValid, setGstDetailsValid] = useState(true);
    const [gstDetailsSubmitAction, setGstDetailsSubmitAction] = useState(true);
    const [formConfigsMap, setFormConfigsMap] = useState<FormConfigsMap>({});
    const [userAccessList, setUserAccessList] = useState<any[]>([]);

    useEffect(() => {
        initialiseAckoAnalytics();
    }, [router?.pathname]);

    useEffect(() => {
        if (userDetails.user_id) {
            /**
             * identify segment event call
             */
            identifyEvent(userDetails.user_id, {
                phone: userDetails.phone,
            });
        }
    }, [userDetails]);

    useEffect(() => {
        if (
            router.pathname.includes('/login') ||
            router.pathname.includes('/logout') ||
            router.pathname.includes('/partners')
        ) {
            setIsLoading(false);
            return;
        }

        const initialiseUserAccessAndPermissions = async () => {
            try {
                const { user_access, user_details } = await fetchUserAccess();
                const latestAccessList = user_access || [];

                saveUserAccess(latestAccessList);
                setUserAccessList(latestAccessList);

                if (user_details) {
                    setUserDetails((prevState: any) => ({
                        ...prevState,
                        ...user_details,
                    }));
                }

                const selectedPartnerContext = getPartnerContext();
                const selectedPartnerId = selectedPartnerContext?.partner_id;
                const selectedGroupName =
                    selectedPartnerContext?.group?.group_name;

                const matchedPartner = latestAccessList.find(
                    (access: any) => access?.partner_id === selectedPartnerId
                );
                const matchedGroup = matchedPartner?.groups?.find(
                    (group: any) => group?.group_name === selectedGroupName
                );
                const finalRoutePath = getBootstrapFinalRoutePath({
                    router,
                    partnerId: selectedPartnerId,
                });

                if (!matchedPartner || !matchedGroup) {
                    setIsLoading(false);
                    router.push('/partners');
                    return;
                }

                bootstrapUserPermissions({
                    router,
                    groupName: selectedGroupName,
                    partnerId: selectedPartnerId,
                    finalRoutePath,
                    setPermissions,
                    setUserDetails,
                    setMetaData,
                    setPartnerId,
                    setShowErrorScreen,
                    setPermissionsMap,
                    setDealerType,
                    setLoggedUserRole,
                    setLegalDetailsUploadedStatus,
                    setActiveStage,
                    setGstDetailsValid,
                    setGstDetailsSubmitAction,
                    setFormConfigsMap,
                    setSnackbar,
                    setErrorMessage,
                    setIsLoading,
                });
            } catch (e: any) {
                if (e?.response?.status === 401) {
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page',
                    });
                    setIsLoading(false);
                    router.push('/login');
                    return;
                }
                setErrorMessage(
                    e?.response?.data?.message || 'Failed to fetch user access'
                );
                setShowErrorScreen(true);
                setIsLoading(false);
            }
        };

        initialiseUserAccessAndPermissions();
    }, []);

    useEffect(() => {
        initGlobals();
        // Set tracker ID cookie if not present
        setTrackerIdCookie();
    }, []);

    if (isLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (showErrorScreen) {
        return <ErrorPage errorMessage={errorMessage} />;
    }

    return (
        <ThemeProvider theme={AckoMainTheme}>
            <MPosContext.Provider
                value={{
                    cookie,
                    partnerId,
                    userDetails,
                    permissions,
                    metaData,
                    dealerType,
                    showHamburger,
                    permissionsMap,
                    activeStage: stage,
                    planDetails,
                    legalDetailsUploadedStatus,
                    loggedUserRole,
                    gstDetailsValid,
                    gstDetailsSubmitAction,
                    formConfigsMap,
                    userAccessList,
                    setPlanDetails,
                    setUserAccessList,
                    setActiveStage,
                    setPermissions,
                    setUserDetails,
                    setPartnerId,
                    setShowHamburger,
                    setSnackbar,
                    setLoggedUserRole,
                    setLegalDetailsUploadedStatus,
                    setGstDetailsValid,
                    setMetaData,
                    setPermissionsMap,
                    setGstDetailsSubmitAction,
                    setFormConfigsMap,
                }}
            >
                <ViewportProvider isMobileDevice={isMobileDevice}>
                    {showHamburger ? (
                        <Hamburger />
                    ) : (
                        <>
                            <Component
                                {...pageProps}
                                query={query}
                                host={host}
                                cookie={cookie}
                            />
                            <SnackBar
                                {...snackbar}
                                handleSnakbarComplete={() =>
                                    setSnackbar({ type: '', message: '' })
                                }
                            />
                        </>
                    )}
                </ViewportProvider>
            </MPosContext.Provider>
        </ThemeProvider>
    );
}

MPosApp.getInitialProps = async (context: any) => {
    const appProps = await App.getInitialProps(context);
    const userAgent =
        typeof window === 'undefined'
            ? context?.ctx?.req?.headers['user-agent']
            : window?.navigator?.userAgent;

    const isMobileDevice = isMobile(userAgent).any;

    return {
        ...appProps,
        isMobileDevice,
        query: context?.ctx?.query,
        host: context?.ctx?.req?.headers?.host,
        cookie: context?.ctx?.req?.headers?.cookie || '',
    };
};

export default MPosApp;
